import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { apiErrorMessage, useRegister } from '../lib/api'
import { AuthShell, ArrowRightIcon } from '../components/AuthShell'
import { PasswordStrength } from '../components/PasswordStrength'

const COUNTRIES: Array<{ code: string; label: string }> = [
  { code: 'HU', label: 'Hungary' }, { code: 'AT', label: 'Austria' }, { code: 'DE', label: 'Germany' },
  { code: 'GB', label: 'United Kingdom' }, { code: 'IE', label: 'Ireland' }, { code: 'ES', label: 'Spain' },
  { code: 'IT', label: 'Italy' }, { code: 'FR', label: 'France' }, { code: 'NL', label: 'Netherlands' },
  { code: 'CA', label: 'Canada' }, { code: 'US', label: 'United States' }, { code: 'AU', label: 'Australia' },
]

export function RegisterPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [countryCode, setCountryCode] = useState('HU')
  // Default unchecked — the 18+ / Terms confirmation has to be an explicit
  // affirmative act. Pre-checking it would defeat the purpose of the consent.
  const [agreed, setAgreed] = useState(false)
  const reg = useRegister()
  const nav = useNavigate()

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!agreed) return
    reg.mutate(
      { email, password, displayName, countryCode, acceptedTerms: true },
      { onSuccess: () => nav('/login', { replace: true }) },
    )
  }

  return (
    <AuthShell>
      <div className="auth-card">
        <div className="auth-card__header">
          <h1 className="auth-card__title">Create your account</h1>
          <p className="auth-card__subtitle">
            Start with a Pro Challenge in under a minute. No credit card needed to explore the dashboard.
          </p>
        </div>

        <form className="auth-card__body" onSubmit={submit}>
          <div className="field">
            <label className="field__label" htmlFor="signup-name">Display name</label>
            <input id="signup-name" className="field__input" type="text" required autoComplete="name"
              placeholder="Andrew Le" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="signup-email">Email address</label>
            <input id="signup-email" className="field__input" type="email" required autoComplete="email"
              placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="signup-pw">Password</label>
            <input id="signup-pw" className="field__input" type="password" required minLength={12}
              autoComplete="new-password" placeholder="12+ chars · 1 digit · 1 symbol"
              value={password} onChange={(e) => setPassword(e.target.value)} />
            {password && <PasswordStrength password={password} />}
          </div>

          <div className="field">
            <label className="field__label" htmlFor="signup-country">Country of residence</label>
            <select id="signup-country" className="field__select"
              value={countryCode} onChange={(e) => setCountryCode(e.target.value)}>
              {COUNTRIES.map((c) => (<option key={c.code} value={c.code}>{c.label}</option>))}
            </select>
            <span className="field__helper">Used for compliance.</span>
          </div>

          <label className="auth-card__terms">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
            <span>
              I agree to the <Link to="/terms">Terms of Service</Link>, <Link to="/privacy">Privacy Policy</Link>,
              and confirm I am 18+ and not located in a restricted country.
            </span>
          </label>

          {reg.isError && (
            <div className="auth-card__error" role="alert" style={{ display: 'flex' }}>
              {apiErrorMessage(reg.error)}
            </div>
          )}

          <button type="submit" className="btn btn--primary auth-card__submit"
            disabled={reg.isPending || !agreed}>
            {reg.isPending ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <div className="auth-card__footer">
          Already have an account?
          <Link to="/login" className="auth-card__footer-link">
            Sign in
            <ArrowRightIcon />
          </Link>
        </div>
      </div>
    </AuthShell>
  )
}
